import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "../../lib/projects";
import { useIsMobile, usePrefersReducedMotion } from "../../hooks/useIsMobile";

const DESKTOP_LAYERS = 20;
const MOBILE_LAYERS = 6;

// Mobile-only constants — do not change desktop behaviour.
// On mobile we cap the loader at 2s total and shorten the exit fade.
// iOS Safari aggressively throttles requestAnimationFrame (e.g. low-power mode,
// background tab), which could previously leave the loader mounted forever.
// A wall-clock setTimeout is immune to that throttling.
const MOBILE_MAX_MS = 2000;
const MOBILE_FILL_MS = 1400; // fill animation duration on mobile
const MOBILE_EXIT_MS = 450; // fade-out duration on mobile

// Loader: extruded 3D Mad House logo with red filling up inside the letters.
// Exit is a seamless cinematic crossfade — the black overlay softly fades out
// while blurring and expanding, so the landing page emerges naturally through it.
export const Loader = ({ onDone }) => {
    const isMobile = useIsMobile();
    const reduceMotion = usePrefersReducedMotion();

    const [pct, setPct] = useState(0);
    const [exiting, setExiting] = useState(false);
    const [done, setDone] = useState(false);

    // Device-adaptive visual complexity. Keep the exact desktop look intact
    // but drop layer count + slow the idle rotation on mobile for a smooth
    // 60fps on low-end iOS/Android GPUs.
    const layers = isMobile ? MOBILE_LAYERS : DESKTOP_LAYERS;
    const layerOffset = isMobile ? 5 : 4; // pixels of depth per layer
    const idleDuration = isMobile ? 8 : 6; // slower rotation on mobile = less compositing churn
    const idleRotateY = isMobile ? [-18, 18, -18] : [-35, 35, -35];
    const idleRotateX = isMobile ? [-6, 6, -6] : [-10, 10, -10];
    const idleRotateZ = isMobile ? [-1, 1, -1] : [-2, 2, -2];
    const entryBlur = isMobile ? "blur(8px)" : "blur(20px)";
    const entryScale = isMobile ? 0.6 : 0.45;

    // Primary fill animation via rAF (visuals). Kept identical on desktop.
    useEffect(() => {
        // When reduced motion is on, skip the fill animation entirely.
        if (reduceMotion) {
            setPct(100);
            return undefined;
        }
        let raf;
        let cancelled = false;
        const start = performance.now();
        const duration = isMobile ? MOBILE_FILL_MS : 2800;
        const tick = (t) => {
            if (cancelled) return;
            const elapsed = t - start;
            const p = Math.min(1, elapsed / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            setPct(eased * 100);
            if (p < 1) raf = requestAnimationFrame(tick);
            else if (!isMobile) {
                // Desktop: original flow (triggers exit after a short beat)
                setTimeout(() => setExiting(true), 450);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (raf) cancelAnimationFrame(raf);
        };
    }, [isMobile, reduceMotion]);

    // Mobile hard-cap: unconditionally trigger exit + unmount within budget.
    // This runs on a wall-clock setTimeout and is immune to rAF throttling.
    useEffect(() => {
        if (!isMobile) return undefined;
        const enterExit = setTimeout(
            () => setExiting(true),
            Math.max(0, MOBILE_MAX_MS - MOBILE_EXIT_MS)
        );
        const forceDone = setTimeout(() => setDone(true), MOBILE_MAX_MS);
        return () => {
            clearTimeout(enterExit);
            clearTimeout(forceDone);
        };
    }, [isMobile]);

    // Reduced-motion shortcut — render briefly then release.
    useEffect(() => {
        if (!reduceMotion) return undefined;
        const t = setTimeout(() => setDone(true), 400);
        return () => clearTimeout(t);
    }, [reduceMotion]);

    // Desktop exit → done
    useEffect(() => {
        if (!exiting || isMobile) return undefined;
        const t = setTimeout(() => setDone(true), 1200);
        return () => clearTimeout(t);
    }, [exiting, isMobile]);

    useEffect(() => {
        if (done) onDone && onDone();
    }, [done, onDone]);

    const exitDuration = isMobile ? MOBILE_EXIT_MS / 1000 : 1.1;

    return (
        <AnimatePresence>
            {!done ? (
                <motion.div
                    key="loader"
                    data-testid="loader-root"
                    className="fixed inset-0 z-[100]"
                    // The entire loader gently fades + blurs to reveal the page
                    animate={
                        exiting
                            ? { opacity: 0, filter: "blur(14px)", scale: 1.04 }
                            : { opacity: 1, filter: "blur(0px)", scale: 1 }
                    }
                    transition={{ duration: exitDuration, ease: [0.22, 1, 0.36, 1] }}
                    style={{ background: "#050505", transformOrigin: "50% 50%" }}
                >
                    {/* SVG filters */}
                    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden>
                        <defs>
                            <filter id="mh-red" colorInterpolationFilters="sRGB">
                                <feColorMatrix
                                    type="matrix"
                                    values="0 0 0 0 0
                                            0 0 0 0 0
                                            0 0 0 0 0
                                            0.34 0.33 0.33 0 0"
                                    result="luma"
                                />
                                <feComponentTransfer in="luma" result="mask">
                                    <feFuncA type="linear" slope="12" intercept="-8.5" />
                                </feComponentTransfer>
                                <feFlood floodColor="#DB162F" result="red" />
                                <feComposite in="red" in2="mask" operator="in" />
                            </filter>

                            {Array.from({ length: layers }).map((_, i) => {
                                const t = i / (layers - 1);
                                const shade = 0.12 + t * 0.55;
                                return (
                                    <filter
                                        key={`dimf-${i}`}
                                        id={`mh-dim-${i}`}
                                        colorInterpolationFilters="sRGB"
                                    >
                                        <feColorMatrix
                                            type="matrix"
                                            values={`${shade} 0 0 0 0
                                                     0 ${shade} 0 0 0
                                                     0 0 ${shade} 0 0
                                                     0 0 0 1 0`}
                                        />
                                    </filter>
                                );
                            })}
                        </defs>
                    </svg>

                    {/* Subtle radial vignette that brightens briefly on exit — like a camera opening */}
                    <motion.div
                        aria-hidden
                        className="pointer-events-none absolute inset-0"
                        style={{
                            background:
                                "radial-gradient(1100px 700px at 50% 50%, rgba(219,22,47,0.08), transparent 70%)"
                        }}
                        animate={exiting ? { opacity: [0.5, 1.2, 0] } : { opacity: 0.5 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    />

                    {/* 3D rig */}
                    <motion.div
                        className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center"
                        style={{ perspective: "700px" }}
                    >
                        <motion.div
                            className="relative h-72 w-72 md:h-96 md:w-96 lg:h-[30rem] lg:w-[30rem]"
                            initial={{
                                rotateY: -200,
                                rotateX: -25,
                                scale: entryScale,
                                opacity: 0,
                                filter: entryBlur
                            }}
                            animate={{
                                rotateY: idleRotateY,
                                rotateX: idleRotateX,
                                rotateZ: idleRotateZ,
                                scale: 1,
                                opacity: 1,
                                filter: "blur(0px)"
                            }}
                            transition={{
                                opacity: { duration: 1.1, ease: "easeOut" },
                                filter: { duration: 1.1, ease: "easeOut" },
                                scale: { duration: 1.2, ease: [0.22, 1, 0.36, 1] },
                                rotateY: { duration: idleDuration, repeat: Infinity, ease: "easeInOut" },
                                rotateX: { duration: idleDuration, repeat: Infinity, ease: "easeInOut" },
                                rotateZ: { duration: idleDuration, repeat: Infinity, ease: "easeInOut" }
                            }}
                        >
                            {/* Extruded depth stack */}
                            {Array.from({ length: layers }).map((_, i) => {
                                const depth = -(layers - 1 - i) - 1;
                                return (
                                    <img
                                        key={`layer-${i}`}
                                        src={LOGO_URL}
                                        alt=""
                                        aria-hidden
                                        draggable={false}
                                        className="absolute inset-0 h-full w-full select-none object-contain"
                                        style={{
                                            transform: `translate3d(0, 0, ${depth * layerOffset}px)`,
                                            filter: `url(#mh-dim-${i})`
                                        }}
                                    />
                                );
                            })}

                            {/* Front face: WHITE logo */}
                            <img
                                src={LOGO_URL}
                                alt="Mad House Studios"
                                draggable={false}
                                className="absolute inset-0 h-full w-full select-none object-contain"
                                style={{
                                    transform: "translate3d(0, 0, 0px)",
                                    filter: "drop-shadow(0 12px 24px rgba(0,0,0,0.6))"
                                }}
                            />

                            {/* Front face: RED-tinted logo, clipped from bottom up */}
                            <motion.img
                                src={LOGO_URL}
                                alt=""
                                aria-hidden
                                draggable={false}
                                className="absolute inset-0 h-full w-full select-none object-contain"
                                animate={{
                                    clipPath: `inset(${100 - pct}% 0 0 0)`,
                                    WebkitClipPath: `inset(${100 - pct}% 0 0 0)`
                                }}
                                transition={{ duration: 0.08, ease: "linear" }}
                                style={{
                                    transform: "translate3d(0, 0, 2px)",
                                    filter: "url(#mh-red)"
                                }}
                            />
                        </motion.div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
};

export default Loader;
